import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SHIFTIQ_API, getAuthHeaders } from "../../api/client";
import { SHIFTIQ_ENABLED } from "../../features";
import { PeopleCostRevenueHint } from "./scheduleComponents";

function StoreSwitcherSearchInput({ onQuery, inputRef }) {
  const [value, setValue] = useState("");
  useEffect(() => {
    const id = window.setTimeout(() => onQuery(value), 100);
    return () => window.clearTimeout(id);
  }, [value, onQuery]);
  return (
    <input
      ref={inputRef}
      type="text"
      placeholder="Search stores..."
      value={value}
      onChange={(e) => setValue(e.target.value)}
      style={{
        width: "100%",
        padding: "10px 14px",
        borderRadius: 10,
        border: "1.5px solid var(--border)",
        fontSize: 13,
        background: "var(--surface2)",
        color: "var(--text-primary)",
        marginBottom: 16,
        boxSizing: "border-box",
      }}
    />
  );
}

const StoreSwitcherDialog = forwardRef(function StoreSwitcherDialog({
  stores,
  currentStore,
  weekStartISO,
  employees,
  totalIssueCount,
  lang,
  onSelect,
  openRef,
}, ref) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hoverStore, setHoverStore] = useState(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [summaries, setSummaries] = useState({});
  const [summariesLoading, setSummariesLoading] = useState(false);
  const inputRef = useRef(null);
  const onQuery = useCallback((value) => setQuery(value), []);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setHoverStore(null);
  }, []);

  useImperativeHandle(ref, () => ({
    open: () => {
      if ((stores || []).length > 1) setOpen(true);
    },
    close,
  }), [stores, close]);

  useEffect(() => {
    if (openRef) openRef.current = open;
  }, [open, openRef]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (stores || []).filter((name) => !q || name.toLowerCase().includes(q));
  }, [stores, query]);

  const staffCountByStore = useMemo(() => {
    const counts = {};
    (employees || []).forEach((person) => {
      const name = person?.store;
      if (!name) return;
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [employees]);

  useEffect(() => {
    if (!open) return undefined;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open || !weekStartISO) return undefined;
    let cancelled = false;
    setSummariesLoading(true);
    fetch(`${SHIFTIQ_API}/stores/summary?week_start=${encodeURIComponent(weekStartISO)}`, {
      headers: getAuthHeaders(),
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (cancelled) return;
        const map = {};
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          if (row?.store_id) map[row.store_id] = row;
        });
        setSummaries(map);
      })
      .catch(() => {
        if (!cancelled) setSummaries({});
      })
      .finally(() => {
        if (!cancelled) setSummariesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, weekStartISO]);

  useEffect(() => {
    if (!open) return;
    const idx = filtered.indexOf(currentStore);
    setFocusIndex(idx >= 0 ? idx : 0);
  }, [open, query, currentStore, filtered]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        const pick = filtered[focusIndex];
        if (pick) onSelect(pick);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, filtered, focusIndex, onSelect, close]);

  if (!open) return null;

  return createPortal(
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="store-switcher-title"
        style={{
          background: "var(--surface)",
          borderRadius: 16,
          width: "min(640px, 90vw)",
          maxHeight: "80vh",
          overflow: "hidden",
          boxShadow: "0 16px 48px rgba(0,0,0,0.16)",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          paddingBottom: 16,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, padding: "24px 24px 20px" }}>
          <div id="store-switcher-title" style={{ fontSize: 18, fontWeight: 800, color: "var(--text-primary)" }}>
            Select Store
          </div>
          <button type="button" aria-label="Close" onClick={close} style={{ border: "none", background: "none", fontSize: 18, cursor: "pointer", color: "var(--text-muted)" }}>
            ✕
          </button>
        </div>
        <div style={{ flexShrink: 0, padding: "0 24px" }}>
          <StoreSwitcherSearchInput onQuery={onQuery} inputRef={inputRef} />
        </div>
        <div
          className="store-switcher-scroll"
          style={{
            overflowX: "hidden",
            overflowY: "auto",
            flex: 1,
            minHeight: 0,
            padding: "12px 24px 8px",
          }}
        >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
          {filtered.map((storeName, idx) => {
            const isSel = storeName === currentStore;
            const summary = summaries[storeName];
            const staffHere = summariesLoading
              ? null
              : Number(summary?.staff_count ?? staffCountByStore[storeName] ?? 0);
            const pctRaw = summariesLoading ? null : summary?.people_cost_pct;
            const pct = pctRaw != null && Number.isFinite(Number(pctRaw)) ? Number(pctRaw) : null;
            const cardCostIssues = summariesLoading ? null : Number(summary?.cost_issue_count ?? 0);
            const hasAnyIssues = !summariesLoading && (isSel ? totalIssueCount > 0 || cardCostIssues > 0 : cardCostIssues > 0);
            const dotColor = !SHIFTIQ_ENABLED
              ? (summariesLoading ? "var(--border)" : staffHere > 0 ? "var(--green)" : "var(--text-muted)")
              : pct != null && pct > 30
                ? "#EF4444"
                : pct == null
                  ? "var(--text-muted)"
                  : pct > 22
                    ? "#F59E0B"
                    : "#22C55E";
            const barColor = !SHIFTIQ_ENABLED
              ? "var(--border)"
              : pct != null && pct > 30
                ? "#EF4444"
                : pct == null
                  ? "var(--border)"
                  : pct > 22
                    ? "#F59E0B"
                    : "#22C55E";
            const barPct = pct != null ? Math.min((pct / 30) * 100, 100) : 0;
            const isHover = hoverStore === storeName;
            return (
              <div
                key={storeName}
                role="button"
                tabIndex={0}
                onMouseEnter={() => setHoverStore(storeName)}
                onMouseLeave={() => setHoverStore((h) => (h === storeName ? null : h))}
                onClick={() => onSelect(storeName)}
                style={{
                  position: "relative",
                  background: isSel || isHover ? "var(--green-light)" : "var(--surface2)",
                  border: isSel ? "2px solid var(--green)" : `1.5px solid ${isHover ? "var(--green)" : "var(--border)"}`,
                  borderRadius: 12,
                  padding: 16,
                  cursor: "pointer",
                  boxShadow: focusIndex === idx ? "0 0 0 2px var(--green)" : "none",
                }}
              >
                {isSel ? (
                  <span style={{ position: "absolute", top: 10, right: 10, fontSize: 9, fontWeight: 800, background: "var(--green)", color: "#fff", borderRadius: 4, padding: "2px 6px" }}>
                    Currently viewing
                  </span>
                ) : null}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, gap: 8, paddingRight: isSel ? 108 : 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: "var(--text-primary)" }}>{storeName}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 12, height: 12, borderRadius: "50%", background: dotColor }} />
                    {SHIFTIQ_ENABLED ? (
                      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}>
                        {summariesLoading ? "…" : pct != null ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                            {`${pct.toFixed(1)}%`}
                            <PeopleCostRevenueHint basis={summary?.revenue_basis} amount={summary?.revenue_amount} lang={lang} />
                          </span>
                        ) : "—"}
                      </span>
                    ) : null}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {summariesLoading ? "…" : `${staffHere} staff`}
                  {SHIFTIQ_ENABLED && !summariesLoading ? (
                    hasAnyIssues ? ` · ${isSel ? totalIssueCount : cardCostIssues} issues` : " · No issues"
                  ) : null}
                </div>
                {SHIFTIQ_ENABLED ? (
                  <div style={{ width: "100%", height: 4, background: "var(--border)", borderRadius: 2, marginTop: 10, overflow: "hidden" }}>
                    <div style={{ width: `${barPct}%`, height: 4, background: barColor }} />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
});

export default StoreSwitcherDialog;
